import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SavedDesignsProvider } from "@/context/SavedDesignsContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicAccessGuard from "@/components/PublicAccessGuard";
import ScrollManager from "@/components/ScrollManager";
import { useSiteMode } from "@/hooks/useSiteMode";

// ─── EAGER (above the fold / common to most sessions) ────────────────────────
// These bundles are needed for the first render the average visitor sees, so
// pulling them lazily would just add an extra round-trip with no benefit.
import LoginPage from "@/pages/LoginPage";
import CatalogPage from "@/pages/CatalogPage";
import SeriesDetailPage from "@/pages/SeriesDetailPage";

// ─── LAZY (rarely-visited or admin-only) ─────────────────────────────────────
// These pages add ~60-80 KB each to the JS bundle but are never opened by a
// typical retail visitor. Code-splitting cuts ~30-40% off the initial download
// on first paint, which is the metric that matters most on slow Indian mobile.
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const CardDetailPage = lazy(() => import("@/pages/CardDetailPage"));
const OtpVerifyPage = lazy(() => import("@/pages/OtpVerifyPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const HowToRegisterPage = lazy(() => import("@/pages/HowToRegisterPage"));
const SavedDesignsPage = lazy(() => import("@/pages/SavedDesignsPage"));
const InstallPage = lazy(() => import("@/pages/InstallPage"));
const SharedCatalogPage = lazy(() => import("@/pages/SharedCatalogPage"));

// Admin chunks — only loaded after admin authenticates. Public visitors never
// download any of this.
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminSeriesPage = lazy(() => import("@/pages/AdminSeriesPage"));
const AdminSeriesDetailPage = lazy(() => import("@/pages/AdminSeriesDetailPage"));
const AdminImportPage = lazy(() => import("@/pages/AdminImportPage"));
const AdminCustomersPage = lazy(() => import("@/pages/AdminCustomersPage"));
const AdminStoragePage = lazy(() => import("@/pages/AdminStoragePage"));
const AdminResetRequestsPage = lazy(() => import("@/pages/AdminResetRequestsPage"));
const AdminAlertsPage = lazy(() => import("@/pages/AdminAlertsPage"));
const AdminVisitorsPage = lazy(() => import("@/pages/AdminVisitorsPage"));
const AdminContactsPage = lazy(() => import("@/pages/AdminContactsPage"));
const AdminShareLinksPage = lazy(() => import("@/pages/AdminShareLinksPage"));
const AdminRetailPricingPage = lazy(() => import("@/pages/AdminRetailPricingPage"));

// Shared fallback shown while a lazy chunk is downloading. Kept deliberately
// minimal to avoid a flash of branded content on top of the actual page that's
// about to take over.
const PageLoading = () => (
  <div className="min-h-[40vh] flex items-center justify-center" data-testid="lazy-route-loading">
    <span className="font-serif text-[#D62828] text-xl">Loading…</span>
  </div>
);

const RootRedirect = () => {
  const { user, loading } = useAuth();
  const { loading: modeLoading, publicCatalogEnabled } = useSiteMode();
  if (loading || modeLoading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><span className="font-serif text-[#D62828] text-xl">Loading…</span></div>;
  // Logged-in admins jump to their dashboard; everyone else (including unauth visitors
  // and approved customers) lands on the public catalog — UNLESS the admin has
  // flipped public catalog OFF, in which case unauth visitors get the login page.
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (!user && !publicCatalogEnabled) return <Navigate to="/login" replace />;
  return <Navigate to="/catalog" replace />;
};

function App() {
  // Global save-image protection: block right-click / long-press save on every <img>.
  // Customer flows display proprietary catalog photos — we want to make casual saving hard.
  useEffect(() => {
    const blockOnImage = (e) => {
      if (e.target && e.target.tagName === "IMG") e.preventDefault();
    };
    document.addEventListener("contextmenu", blockOnImage);
    document.addEventListener("dragstart", blockOnImage);
    return () => {
      document.removeEventListener("contextmenu", blockOnImage);
      document.removeEventListener("dragstart", blockOnImage);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SavedDesignsProvider>
            <ScrollManager />
            <Toaster position="top-right" richColors />
            <Suspense fallback={<Layout><PageLoading /></Layout>}>
              <Routes>
                <Route path="/login" element={<Layout><LoginPage /></Layout>} />
                <Route path="/install" element={<InstallPage />} />
                <Route path="/shared/:token" element={<SharedCatalogPage />} />
                <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
                <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />

                <Route path="/" element={<Layout><RootRedirect /></Layout>} />
                <Route path="/verify-otp" element={<Layout><OtpVerifyPage /></Layout>} />

                <Route path="/catalog" element={<PublicAccessGuard><Layout><CatalogPage /></Layout></PublicAccessGuard>} />
                <Route path="/saved" element={<PublicAccessGuard><Layout><SavedDesignsPage /></Layout></PublicAccessGuard>} />
                <Route path="/contact" element={<PublicAccessGuard><Layout><ContactPage /></Layout></PublicAccessGuard>} />
                <Route path="/how-to-register" element={<PublicAccessGuard><Layout><HowToRegisterPage /></Layout></PublicAccessGuard>} />
                <Route path="/series/:id" element={<PublicAccessGuard><Layout><SeriesDetailPage /></Layout></PublicAccessGuard>} />
                <Route path="/cards/:id" element={<ProtectedRoute role="customer"><Layout><CardDetailPage /></Layout></ProtectedRoute>} />

                <Route path="/admin" element={<ProtectedRoute role="admin"><Layout><AdminDashboardPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/cards" element={<ProtectedRoute role="admin"><Layout><AdminSeriesPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/series/:id" element={<ProtectedRoute role="admin"><Layout><AdminSeriesDetailPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/import" element={<ProtectedRoute role="admin"><Layout><AdminImportPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/customers" element={<ProtectedRoute role="admin"><Layout><AdminCustomersPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/reset-requests" element={<ProtectedRoute role="admin"><Layout><AdminResetRequestsPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/alerts" element={<ProtectedRoute role="admin"><Layout><AdminAlertsPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/visitors" element={<ProtectedRoute role="admin"><Layout><AdminVisitorsPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/contacts" element={<ProtectedRoute role="admin"><Layout><AdminContactsPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/storage" element={<ProtectedRoute role="admin"><Layout><AdminStoragePage /></Layout></ProtectedRoute>} />
                <Route path="/admin/share-links" element={<ProtectedRoute role="admin"><Layout><AdminShareLinksPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/retail-pricing" element={<ProtectedRoute role="admin"><Layout><AdminRetailPricingPage /></Layout></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </SavedDesignsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
